
import { useEffect, useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { checkUserAuthentication, syncLocationWithProfile, loadLocationFromProfile } from './profile-sync';
import { createLogger } from "@/utils/logger";

const logger = createLogger('location:sync');

/**
 * Hook for synchronizing location with user profile
 */
export const useLocationSync = (
  locationCore,
  hasUserProfile,
  profileSyncedRef,
  userProfileRef,
  setHasUserProfile,
  staleTolerance
) => {
  const { locationState, saveLocation } = locationCore;
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // Check if user is authenticated and load profile on mount
  useEffect(() => {
    // Don't run until location state is initialized
    if (!locationState.initialized) {
      logger.debug('Location state not initialized, delaying profile load');
      return;
    }

    const initializeUserProfile = async () => {
      if (syncInProgress) {
        logger.debug('Sync already in progress, skipping');
        return;
      }
      
      try {
        setSyncInProgress(true);
        logger.debug('Initializing user profile...');
        const { isAuthenticated, profile } = await checkUserAuthentication();
        
        if (isAuthenticated) {
          setHasUserProfile(true);
          userProfileRef.current = profile;
          
          // Try to load location from the profile first
          const loadProfileLocation = async () => {
            try {
              logger.debug('Attempting to load location from profile');
              const loaded = await loadLocationFromProfile(
                staleTolerance, 
                saveLocation
              );
              
              logger.debug(`Location loaded from profile: ${loaded}`);
              
              if (!loaded && locationState.permission === 'granted') {
                // If we couldn't load from profile but we have permission, update profile with current location
                if (locationState.coords) {
                  logger.debug('Profile location not available but we have browser location, syncing to profile');
                  await syncLocationWithProfile(locationState.coords);
                }
              }
              
              setProfileLoaded(true);
            } catch (profileError) {
              logger.warn('Error loading profile location:', profileError);
              setProfileLoaded(true);
            }
          };
          
          await Promise.resolve(loadProfileLocation());
        } else {
          logger.debug('User not authenticated, skipping profile sync');
          setProfileLoaded(true);
        }
      } catch (err) {
        logger.warn('Error initializing user profile:', err);
        setProfileLoaded(true);
      } finally {
        setSyncInProgress(false);
      }
    };
    
    // Ensure proper promise handling with explicit Promise cast
    const initPromise = initializeUserProfile();
    Promise.resolve(initPromise).catch(err => {
      logger.warn('Unhandled error in initializeUserProfile:', err);
      setSyncInProgress(false);
      setProfileLoaded(true);
    });
  }, [
    locationState.permission, 
    locationState.coords, 
    locationState.initialized, // Added dependency on initialization
    saveLocation, 
    staleTolerance,
    setHasUserProfile,
    syncInProgress
  ]);

  // Function to sync location with profile
  const syncWithProfile = async (coords) => {
    if (!hasUserProfile || profileSyncedRef.current) {
      return false;
    }
    
    try {
      logger.debug('Syncing location with profile');
      const success = await syncLocationWithProfile(coords);
      if (success) {
        profileSyncedRef.current = true;
        logger.debug('Successfully synced location with profile');
      } else {
        logger.debug('Failed to sync location with profile');
      }
      return success;
    } catch (error) {
      logger.warn('Error syncing with profile:', error);
      return false;
    }
  };

  return {
    syncWithProfile,
    profileLoaded
  };
};
